import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, startWith } from 'rxjs';
import { AuthService } from '@cnt-workspace/data-access';
import { hasOwnActionBar } from './bottom-nav-routes';

/**
 * Whether the phone bottom nav is on screen: a signed-in user on a route with no
 * bottom action bar of its own. The nav is `md:hidden`, so this says nothing
 * about width; consumers still limit their adjustment to phones. Anything that
 * pins itself to the bottom of the screen (the cookie banner, the article share
 * strip, the app shell's bottom padding) reads this instead of assuming the nav
 * is always there.
 */
@Injectable({ providedIn: 'root' })
export class BottomNavVisibilityService {
  private auth = inject(AuthService);
  private router = inject(Router);

  private readonly visibleSubject = new BehaviorSubject<boolean>(false);
  readonly visible$ = this.visibleSubject.asObservable();

  constructor() {
    const url$ = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url),
    );
    combineLatest([this.auth.currentUser$, url$])
      .pipe(
        map(([user, url]) => !!user && !hasOwnActionBar(url)),
        distinctUntilChanged(),
      )
      .subscribe((visible) => this.visibleSubject.next(visible));
  }

  get visible(): boolean {
    return this.visibleSubject.value;
  }
}
